/**
 * VoiceAssistant - in-platform live-voice entry point (reusable).
 *
 * This is THE component that lets a user talk to the platform's voice
 * assistant from inside the browser. A "Talk to us" trigger opens a call
 * panel that connects to a live voice session and streams the user's mic
 * over WebRTC. It is intentionally generic and reusable: it takes no
 * tenant-specific copy or config, renders the same everywhere, and can be
 * dropped into any admin or consumer surface. It is currently mounted on
 * the admin Claims page for testing; moving it to the consumer claim flow
 * later is just a matter of rendering <VoiceAssistant /> there.
 *
 * Vendor neutrality: nothing user-facing here names the voice provider. The
 * single vendor reference is the SDK import below (`retell-client-js-sdk`),
 * which is unavoidable; the session token comes from our own generic
 * `/api/voice/web-session/` endpoint and the secret API key never reaches
 * the browser.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { RetellWebClient, type TransportKind } from 'retell-client-js-sdk'
import { Mic, PhoneCall, PhoneOff, Loader2, AudioLines } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'
import { fetchVoiceSession } from '@/shared/lib/voice/use-voice-session'
import { extractApiErrorMessage } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'

type CallState = 'idle' | 'connecting' | 'live' | 'ended' | 'error'

// MM:SS for the live-call timer.
function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export interface VoiceAssistantProps {
  /** Trigger button label. Defaults to the generic "Talk to us". */
  label?: string
  /** Optional className passed to the trigger button. */
  className?: string
}

export function VoiceAssistant({
  label = 'Talk to us',
  className,
}: VoiceAssistantProps) {
  const [open, setOpen] = useState(false)
  const [callState, setCallState] = useState<CallState>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [agentSpeaking, setAgentSpeaking] = useState(false)
  const [elapsed, setElapsed] = useState(0)

  // One client instance per mounted widget; created lazily on first call.
  const clientRef = useRef<RetellWebClient | null>(null)

  // Mirror `open` into a ref so the async startCall flow can read the current
  // open state without a stale closure. Prevents a live call (and mic
  // capture) starting after the dialog was closed mid-fetch.
  const openRef = useRef(open)
  useEffect(() => {
    openRef.current = open
  }, [open])

  const getClient = useCallback((): RetellWebClient => {
    if (clientRef.current) return clientRef.current
    const client = new RetellWebClient()
    client.on('call_started', () => setCallState('live'))
    client.on('call_ended', () => {
      setCallState('ended')
      setAgentSpeaking(false)
    })
    // Not sent on every transport; without them the panel just says Listening.
    client.on('agent_start_talking', () => setAgentSpeaking(true))
    client.on('agent_stop_talking', () => setAgentSpeaking(false))
    client.on('error', (err: unknown) => {
      // Surface a generic failure and tear the call down.
      setErrorMessage('The voice connection dropped. Please try again.')
      setCallState('error')
      setAgentSpeaking(false)
      try {
        client.stopCall()
      } catch {
        // already stopped
      }
      console.error('Voice assistant error:', err)
    })
    clientRef.current = client
    return client
  }, [])

  const startCall = useCallback(async () => {
    setErrorMessage(null)
    setElapsed(0)
    setCallState('connecting')
    try {
      const session = await fetchVoiceSession()
      // Bail if the dialog was closed while the token was in flight. Don't
      // open a WebRTC connection or capture the mic for a closed panel.
      if (!openRef.current) return
      const client = getClient()
      await client.startCall({
        accessToken: session.access_token,
        callId: session.call_id ?? undefined,
        transport: (session.transport as TransportKind | null) ?? undefined,
        iceServers: session.ice_servers ?? undefined,
      })
      // 'call_started' flips us to 'live'.
    } catch (err) {
      // Don't surface an error on a panel the user already closed.
      if (!openRef.current) return
      const apiMessage = extractApiErrorMessage(err)
      setErrorMessage(
        apiMessage ?? 'Could not start the voice assistant. Please try again.',
      )
      setCallState('error')
    }
  }, [getClient])

  const endCall = useCallback(() => {
    const client = clientRef.current
    if (client) {
      try {
        client.stopCall()
      } catch {
        // already stopped
      }
    }
    setCallState('ended')
    setAgentSpeaking(false)
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      if (next) {
        // Auto-connect when the panel opens.
        setCallState('idle')
        void startCall()
      } else {
        // Closing the panel always ends any live call.
        endCall()
        setCallState('idle')
        setErrorMessage(null)
      }
    },
    [startCall, endCall],
  )

  // Safety net: stop the call if the widget unmounts mid-call.
  useEffect(() => {
    return () => {
      const client = clientRef.current
      if (client) {
        try {
          client.stopCall()
        } catch {
          // already stopped
        }
      }
    }
  }, [])

  // Tick the call timer once per second while the call is live.
  useEffect(() => {
    if (callState !== 'live') return
    const id = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(id)
  }, [callState])

  const isBusy = callState === 'connecting' || callState === 'live'

  return (
    <>
      <Button
        variant="outline"
        onClick={() => handleOpenChange(true)}
        className={cn('gap-1.5', className)}
      >
        <Mic size={16} />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Voice assistant</DialogTitle>
            <DialogDescription>
              {callState === 'connecting' && 'Connecting you now…'}
              {callState === 'live' && 'You are connected. Start speaking.'}
              {callState === 'ended' && 'The call has ended.'}
              {callState === 'error' && 'Something went wrong.'}
              {callState === 'idle' && 'Getting things ready…'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-6 py-6">
            {/* Status orb */}
            <div
              className={cn(
                'flex h-24 w-24 items-center justify-center rounded-full border transition-colors',
                callState === 'live'
                  ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                  : callState === 'error'
                    ? 'border-[var(--status-error-border)] bg-[var(--status-error-bg)] text-[var(--status-error)]'
                    : 'border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]',
              )}
            >
              {callState === 'connecting' || callState === 'idle' ? (
                <Loader2 size={32} className="animate-spin" />
              ) : callState === 'live' ? (
                <AudioLines
                  size={32}
                  className={cn(agentSpeaking && 'animate-pulse')}
                />
              ) : (
                <Mic size={32} />
              )}
            </div>

            {/* Call timer */}
            {(callState === 'live' ||
              (callState === 'ended' && elapsed > 0)) && (
              <div className="font-mono text-[12px] tabular-nums tracking-[0.08em] text-[var(--ink-3)]">
                {formatDuration(elapsed)}
              </div>
            )}

            {/* Live indicator */}
            {callState === 'live' && (
              <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] text-[var(--green)]">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--green)] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--green)]" />
                </span>
                {agentSpeaking ? 'Assistant speaking' : 'Listening'}
              </div>
            )}

            {errorMessage && (
              <p className="text-center text-[13px] text-[var(--status-error)]">
                {errorMessage}
              </p>
            )}

            {/* Controls */}
            <div className="flex items-center gap-3">
              {isBusy ? (
                <Button
                  variant="destructive"
                  onClick={endCall}
                  disabled={callState === 'connecting'}
                  className="gap-1.5"
                >
                  <PhoneOff size={16} />
                  End call
                </Button>
              ) : (
                <Button onClick={startCall} className="gap-1.5">
                  <PhoneCall size={16} />
                  {callState === 'ended' || callState === 'error'
                    ? 'Call again'
                    : 'Start call'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
